import i18n from 'i18n';
import {
  ImmunizationVaccinationRecord,
  KonsultaService,
  PatientConsentForm,
  PatientImmunizationTemplate,
  PhilHealthEKAS,
  PhilHealthEPRESS,
  PhilHealthReport
} from 'models';

export const DISEASE_STATUS_OPTIONS = [
  { id: 'disease__no_evidence', value: i18n.t('disease__no_evidence', { ns: 'patient_profile' }) },
  { id: 'disease__stable', value: i18n.t('disease__stable', { ns: 'patient_profile' }) },
  { id: 'disease__progressive', value: i18n.t('disease__progressive', { ns: 'patient_profile' }) },
  { id: 'recurrence_of_disease', value: i18n.t('recurrence_of_disease', { ns: 'patient_profile' }) },
  { id: 'disease__deceased_from_disease', value: i18n.t('disease__deceased_from_disease', { ns: 'patient_profile' }) },
  { id: 'disease__deceased_from_other', value: i18n.t('disease__deceased_from_other', { ns: 'patient_profile' }) },
  {
    id: 'disease__deceased_from_other_unrelated',
    value: i18n.t('disease__deceased_from_other_unrelated', { ns: 'patient_profile' })
  },
  { id: 'disease__status_unknown', value: i18n.t('disease__status_unknown', { ns: 'patient_profile' }) }
];

export const DISEASE_RECURRENCE_OPTIONS = [
  { id: '', value: '---' },
  { id: 'disease__recurrence_general', value: i18n.t('general', { ns: 'patient_profile' }) },
  { id: 'disease__recurrence_regional', value: i18n.t('regional', { ns: 'patient_profile' }) },
  { id: 'disease__recurrence_local', value: i18n.t('local', { ns: 'patient_profile' }) },
  { id: 'disease__recurrence_distant', value: i18n.t('distant', { ns: 'patient_profile' }) },
  { id: 'disease__recurrence_progressive', value: i18n.t('progressive', { ns: 'patient_profile' }) }
];

export const RESPONSE_OPTIONS = [
  { id: '', value: '---' },
  { id: 'response__adverse', value: i18n.t('response__adverse', { ns: 'patient_profile' }) },
  { id: 'response__partial', value: i18n.t('response__partial', { ns: 'patient_profile' }) },
  { id: 'response__complete', value: i18n.t('response__complete', { ns: 'patient_profile' }) },
  {
    id: 'response__no_objective_clinical_benefit',
    value: i18n.t('response__no_objective_clinical_benefit', { ns: 'patient_profile' })
  }
];

export const TREATMENT_STATUS_OPTIONS = [
  { id: '', value: '---' },
  { id: 'treatment__undergoing', value: i18n.t('undergoing_treatment', { ns: 'patient_profile' }) },
  { id: 'treatment__completed', value: i18n.t('treatment__completed', { ns: 'patient_profile' }) },
  { id: 'treatment__incomplete', value: i18n.t('treatment__incomplete', { ns: 'patient_profile' }) },
  { id: 'treatment__none', value: i18n.t('treatment__none', { ns: 'patient_profile' }) },
  { id: 'treatment__status_unknown', value: i18n.t('treatment__status_unknown', { ns: 'patient_profile' }) },
  { id: 'treatment__lost_to_followup', value: 'Lost to follow up' }
];

export const IMMUNIZATION_VACCINATION_RECORD: ImmunizationVaccinationRecord = {
  manufacturer: '',
  site: '',
  vaccine: '',
  vaccineType: '',
  route: '',
  date: null
};

export const PATIENT_IMMUNIZATION_TEMPLATE: PatientImmunizationTemplate = {
  id: undefined,
  patientId: -1,
  templateId: -1,
  patientName: '',
  dateOfBirth: null,
  gender: '',
  nationality: '',
  condition: '',
  signature: '',
  vaccinationRecords: [IMMUNIZATION_VACCINATION_RECORD]
};

export const PATIENT_CONSENT_TEMPLATE: PatientConsentForm = {
  patientName: '',
  patientAddress: '',
  clinicName: ''
};

const EKAS_SERVICE = (serviceName: string): KonsultaService => ({
  serviceName,
  performed: false,
  datePerformed: null,
  performedBy: ''
});

// Fixed EKAS service rows. diagnosticId matches yakapDetail.cons__diagnostic[].p_diagnostic_id (LIB_DIAGNOSTICS ids).
export const EKAS_KONSULTA_SERVICES = [
  { diagnosticId: '1', serviceName: 'Complete Blood Count (CBC) w/ platelet count' },
  {
    diagnosticId: '6',
    serviceName: 'Lipid profile (Total Cholesterol, Triglycerides, HDL Cholesterol, LDL Cholesterol)'
  },
  { diagnosticId: '7', serviceName: 'Fasting Blood Sugar (FBS)' },
  { diagnosticId: '14', serviceName: 'Oral Glucose Tolerance Test' },
  { diagnosticId: '18', serviceName: 'Glycosylated Hemoglobin (HbA1c)' },
  { diagnosticId: '8', serviceName: 'Creatinine' },
  { diagnosticId: '4', serviceName: 'Chest X-Ray' },
  { diagnosticId: '5', serviceName: 'Sputum Microscopy' },
  { diagnosticId: '9', serviceName: 'Electrocardiogram (ECG)' },
  { diagnosticId: '2', serviceName: 'Urinalysis' },
  { diagnosticId: '13', serviceName: 'Pap smear' },
  { diagnosticId: '3', serviceName: 'Fecalysis' },
  { diagnosticId: '15', serviceName: 'Fecal Occult Blood Test' }
];

export const PATIENT_EKAS_TEMPLATE: PhilHealthEKAS = {
  patientName: '',
  atc: '',
  philhealth: '',
  memberStatus: '',
  birthDate: '',
  contactNumber: '',
  hciName: '',
  caseNumber: '',
  hciAccreditationNumber: '',
  transactionNumber: '',
  membershipCategory: '',
  age: '',
  isMember: false,
  isDependent: false,
  konsultaServices: EKAS_KONSULTA_SERVICES.map(({ serviceName }) => ({
    serviceName,
    datePerformed: null,
    performed: '',
    performedBy: ''
  })),
  patientSatisfied: '',
  satisfaction: '',
  comment: ''
};

export const PATIENT_EPRESS_TEMPLATE: PhilHealthEPRESS = {
  patientName: '',
  atc: '',
  philhealth: '',
  memberStatus: '',
  birthDate: '',
  contactNumber: '',
  hciName: '',
  caseNumber: '',
  hciAccreditationNumber: '',
  transactionNumber: '',
  membershipCategory: '',
  age: '',
  isMember: false,
  isDependent: false,
  konsultaMedications: [],
  patientSatisfied: '',
  satisfaction: '',
  comment: ''
};

export const PHILHEALTH_REPORT_TEMPLATE: PhilHealthReport = {
  reportTagging: '',
  reportXML: '',
  reportJSON: ''
};
